import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, MoreVertical,SmilePlus, ChevronLeft, Phone, Video, Check, CheckCheck, Clock, StickyNote, X, Users, Tag, ArrowRightLeft, Search, UserSearch, Loader2, Mic, Trash2, FileText, Lock, AlertCircle, Plus, Image as ImageIcon, Camera, Headphones, UserRound, ListChecks, CalendarDays, Paperclip, Play, Pause, Copy, UserX, Megaphone, ExternalLink, CornerDownLeft, Pencil } from 'lucide-react';
import DOMPurify from 'dompurify';
import EmojiPicker from 'emoji-picker-react';
import api, { getConversation, getMessages, sendMessage, sendMediaMessage, getApprovedTemplates, sendTemplateMessage, getQuickReplies, createQuickReply, updateQuickReply, deleteQuickReply } from '../services/api';
import AppointmentBooking from '../components/AppointmentBooking';
import FunnelStageSelector from '../components/FunnelStageSelector';
import TransferModal from '../components/TransferModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// NOVO: remove o DDI 55 do número do cliente (55 + DDD + telefone = 12 ou 13 dígitos)
const stripCountryCode = (waId) => {
  const digits = String(waId || '').replace(/\D/g, '');
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    return digits.slice(2);
  }
  return digits;
};

const VOICE_WAVEFORM = [
  7, 11, 16, 9, 20, 13, 25, 10, 17, 28, 14, 22, 8, 18, 12, 26,
  15, 30, 11, 21, 9, 17, 24, 13, 29, 10, 19, 14, 23, 8, 16, 27,
  12, 20, 9, 25, 15, 22, 11, 18, 28, 13, 21, 8, 17, 24, 10, 19,
];

const formatFileSize = (bytes) => {
  if (!bytes) return null;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getDateDividerLabel = (timestamp) => {
  const msgDate = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

  if (isSameDay(msgDate, today)) return 'Hoje';
  if (isSameDay(msgDate, yesterday)) return 'Ontem';

  const diffDays = Math.floor((today.setHours(0,0,0,0) - new Date(msgDate).setHours(0,0,0,0)) / 86400000);
  if (diffDays < 7) {
    return msgDate.toLocaleDateString('pt-BR', { weekday: 'long' });
  }
  return msgDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const sanitizeMessage = (text) => {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

// ⭐ NOVO: calcula se a janela de 24h está fechada (ou nunca foi aberta)
// Regra da Meta: só pode mandar texto/mídia livre depois que o CLIENTE mandou
// pelo menos uma mensagem, e só dentro de 24h da última mensagem dele.
function getWindowStatus(messages) {
  const inboundMessages = messages.filter(m => m.direction === 'inbound');

  if (inboundMessages.length === 0) {
    return { blocked: true, reason: 'no_inbound' };
  }

  const lastInbound = inboundMessages[inboundMessages.length - 1];
  const lastTime = new Date(lastInbound.timestamp).getTime();
  const expiresAt = lastTime + 24 * 60 * 60 * 1000;

  if (Date.now() >= expiresAt) {
    return { blocked: true, reason: 'expired' };
  }

  return { blocked: false, reason: null };
}

const AudioMessagePlayer = ({
  message,
  isOutbound,
  conversation,
  currentUser
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [waveform, setWaveform] = useState([]);
  // ⭐ CORRIGIDO: velocidade agora começa com a última escolhida (lembrada no
  // navegador), em vez de sempre voltar pra 1x em cada áudio novo
  const [playbackRate, setPlaybackRate] = useState(() => {
    const saved = parseFloat(localStorage.getItem('audioPlaybackRate'));
    return [1, 1.5, 2].includes(saved) ? saved : 1;
  });

  const cyclePlaybackRate = () => {
    const next = playbackRate === 1 ? 1.5 : playbackRate === 1.5 ? 2 : 1;
    setPlaybackRate(next);
    localStorage.setItem('audioPlaybackRate', String(next));
    if (audioRef.current) {
      audioRef.current.playbackRate = next;
    }
  };

  const avatarUrl = isOutbound
    ? (
        currentUser?.avatar_url ||
        currentUser?.avatar ||
        currentUser?.profile_picture_url ||
        currentUser?.profile_pic ||
        currentUser?.profile_image_url ||
        message.sender_avatar_url ||
        message.sender_avatar
      )
    : (
        message.sender_avatar_url ||
        message.sender_avatar ||
        conversation?.profile_picture_url ||
        conversation?.profile_pic ||
        conversation?.avatar_url ||
        conversation?.profile_image_url
      );

  const avatarName = isOutbound
    ? (
        currentUser?.name ||
        currentUser?.full_name ||
        currentUser?.username ||
        'A'
      )
    : (
        conversation?.profile_name ||
        conversation?.name ||
        conversation?.contact_name ||
        message.sender_name ||
        'Cliente'
      );

  const avatarInitial =
    avatarName.trim().charAt(0).toUpperCase() || 'A';

  const formatAudioTime = (value) => {
    if (!Number.isFinite(value)) return '0:00';

    const minutes = Math.floor(value / 60);
    const seconds = Math.floor(value % 60)
      .toString()
      .padStart(2, '0');

    return `${minutes}:${seconds}`;
  };


  /*
   * Calcula a altura real da voz a partir do arquivo de áudio.
   * Cada barra representa a amplitude RMS de uma parte do áudio.
   */
  useEffect(() => {
    let cancelled = false;
    let audioContext = null;

    const buildRealWaveform = async () => {
      if (!message.media_url) return;

      try {
        const AudioContextClass =
          window.AudioContext || window.webkitAudioContext;

        if (!AudioContextClass) {
          throw new Error('Web Audio API não disponível');
        }

        const response = await fetch(message.media_url);

        if (!response.ok) {
          throw new Error(`Erro HTTP ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();

        audioContext = new AudioContextClass();

        const decodedAudio =
          await audioContext.decodeAudioData(arrayBuffer);

        const barCount = 52;
        const channelCount = decodedAudio.numberOfChannels;
        const samplesPerBar = Math.max(
          1,
          Math.floor(decodedAudio.length / barCount)
        );

        const amplitudes = [];

        for (let bar = 0; bar < barCount; bar++) {
          const startSample = bar * samplesPerBar;
          const endSample = Math.min(
            decodedAudio.length,
            startSample + samplesPerBar
          );

          let sumSquares = 0;
          let sampleCount = 0;

          for (
            let sampleIndex = startSample;
            sampleIndex < endSample;
            sampleIndex++
          ) {
            let mixedSample = 0;

            for (
              let channelIndex = 0;
              channelIndex < channelCount;
              channelIndex++
            ) {
              const channel =
                decodedAudio.getChannelData(channelIndex);

              mixedSample += Math.abs(
                channel[sampleIndex] || 0
              );
            }

            mixedSample /= Math.max(1, channelCount);
            sumSquares += mixedSample * mixedSample;
            sampleCount++;
          }

          const rms = Math.sqrt(
            sumSquares / Math.max(1, sampleCount)
          );

          amplitudes.push(rms);
        }

        const maximum = Math.max(...amplitudes, 0.0001);

        /*
         * Mantém os baixos visíveis e preserva a diferença
         * entre partes baixas e partes altas da voz.
         */
        const heights = amplitudes.map((amplitude) => {
          const normalized = amplitude / maximum;

          // Compressão leve para não apagar as partes baixas.
          const visibleHeight = Math.pow(normalized, 0.58);

          return Math.max(
            4,
            Math.min(29, Math.round(visibleHeight * 29))
          );
        });

        if (!cancelled) {
          setWaveform(heights);
        }
      } catch (error) {
        console.error(
          'Não foi possível calcular a altura real da voz:',
          error
        );

        /*
         * Fallback somente quando o navegador não consegue
         * acessar ou decodificar o arquivo.
         */
        if (!cancelled) {
          setWaveform([
            5, 8, 12, 7, 18, 10, 24, 13, 8, 20, 27, 11,
            17, 7, 24, 13, 9, 18, 29, 15, 8, 21, 12, 25,
            10, 18, 7, 23, 14, 9, 20, 28, 11, 17, 8, 24,
            13, 21, 9, 18, 26, 12, 7, 20, 15, 24, 10, 18,
            7, 14, 22, 10
          ]);
        }
      } finally {
        if (audioContext) {
          try {
            await audioContext.close();
          } catch {
            // O contexto já pode estar encerrado.
          }
        }
      }
    };

    buildRealWaveform();

    return () => {
      cancelled = true;
    };
  }, [message.media_url]);

  const toggleAudio = async () => {
    if (!audioRef.current) return;

    if (audioRef.current.paused) {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error('Não foi possível reproduzir o áudio:', error);
      }
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (event) => {
    const nextTime = Number(event.target.value);

    if (!audioRef.current || !Number.isFinite(nextTime)) return;

    audioRef.current.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const progress = duration > 0
    ? Math.min(1, Math.max(0, currentTime / duration))
    : 0;

  const renderAvatar = () => (
    <div className="relative z-10 h-12 w-12 flex-shrink-0 overflow-visible">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={avatarName}
          className="h-12 w-12 rounded-full object-cover"
          onError={(event) => {
            event.currentTarget.style.display = 'none';

            const fallback =
              event.currentTarget.parentElement.querySelector(
                '[data-audio-avatar-fallback]'
              );

            if (fallback) {
              fallback.style.display = 'flex';
            }
          }}
        />
      ) : null}

      <div
        data-audio-avatar-fallback
        className={`${
          avatarUrl ? 'hidden' : 'flex'
        } h-12 w-12 items-center justify-center rounded-full bg-[#d9fdd3] text-sm font-semibold text-[#128c7e]`}
      >
        {avatarInitial}
      </div>

      <span
        className={`absolute -bottom-1 -right-1 z-20 flex h-[19px] w-[19px] items-center justify-center rounded-full border-2 ${
          isOutbound
            ? 'border-[#128c7e] bg-[#8696a0]'
            : 'border-white bg-[#8696a0]'
        }`}
      >
        <Mic
          size={10}
          strokeWidth={2.5}
          className="text-white"
          fill="currentColor"
        />
      </span>
    </div>
  );

  const renderPlayButton = () => (
    <button
      type="button"
      onClick={toggleAudio}
      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-black hover:bg-black/10"
      aria-label={isPlaying ? 'Pausar áudio' : 'Reproduzir áudio'}
    >
      {isPlaying ? (
        <Pause size={19} fill="currentColor" />
      ) : (
        <Play size={19} fill="currentColor" />
      )}
    </button>
  );

  const renderStatus = () => {
    if (!isOutbound) return null;

    if (message.status === 'read') {
      return <CheckCheck size={13} className="text-[#53bdeb]" />;
    }

    if (message.status === 'delivered') {
      return <CheckCheck size={13} />;
    }

    if (message.status === 'sent') {
      return <Check size={13} />;
    }

    return <Clock size={12} />;
  };

  return (
    <div className="box-border flex h-[56px] w-full min-w-0 max-w-full items-center gap-2 overflow-visible">
      <audio
        ref={audioRef}
        src={message.media_url}
        preload="metadata"
        onLoadedMetadata={(event) => {
          setDuration(event.currentTarget.duration || 0);
          event.currentTarget.playbackRate = playbackRate;
        }}
        onTimeUpdate={(event) => {
          setCurrentTime(event.currentTarget.currentTime || 0);
        }}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
      />

      {/* Mensagem enviada: avatar à esquerda. */}
      {isOutbound && renderAvatar()}

      {renderPlayButton()}

      {/* Esta coluna contém SOMENTE a onda, o tempo e o horário. */}
      <div className="relative h-[48px] min-w-0 flex-1 overflow-hidden">
        <div className="absolute left-0 right-0 top-0 translate-y-[3px] flex h-7 min-w-0 items-center gap-px overflow-hidden">
          {waveform.map((height, index) => {
            const played =
              index / Math.max(1, waveform.length) <= progress;

            return (
              <span
                key={index}
                className={`min-w-0 flex-1 rounded-full ${
                  played
                    ? isOutbound
                      ? 'bg-[#53bdeb]'
                      : 'bg-[#128c7e]'
                    : isOutbound
                      ? 'bg-white/55'
                      : 'bg-[#8696a0]'
                }`}
                style={{
                  height: `${height}px`,
                  maxHeight: '27px'
                }}
              />
            );
          })}

          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.01"
            value={Math.min(currentTime, duration || 0)}
            onChange={handleSeek}
            className="absolute inset-0 h-7 w-full cursor-pointer opacity-0"
            aria-label="Progresso do áudio"
          />
        </div>

        {/* Tempo do áudio e horário ficam dentro da largura da onda. */}
        <div
          className={`absolute bottom-0 left-0 right-0 flex h-3 items-center justify-between whitespace-nowrap text-[10px] leading-none ${
            isOutbound
              ? 'text-white/75'
              : 'text-[#667781]'
          }`}
        >
          <span>
            {formatAudioTime(duration)}
          </span>

          <span className="flex items-center gap-1">
            <span>
              {new Date(message.timestamp).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              })}
            </span>

            {renderStatus()}
          </span>
        </div>
      </div>

      {/* ⭐ NOVO: botão de velocidade, igual o WhatsApp (1x / 1.5x / 2x) */}
      <button
        type="button"
        onClick={cyclePlaybackRate}
        className={`flex h-6 flex-shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
          isOutbound
            ? 'bg-white/15 text-white hover:bg-white/25'
            : 'bg-black/5 text-gray-600 hover:bg-black/10 dark:bg-white/10 dark:text-slate-300'
        }`}
        aria-label="Velocidade de reprodução"
        title="Velocidade de reprodução"
      >
        {playbackRate}x
      </button>

      {/* Mensagem recebida: avatar à direita. */}
      {!isOutbound && renderAvatar()}
    </div>
  );
};

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const MessageBubble = React.memo(({ message, conversation, currentUser }) => {
  const isOutbound = message.direction === 'outbound';
  const [showFullImage, setShowFullImage] = useState(false); // NOVO: abre a imagem em tela cheia ao clicar
  // ⭐ NOVO: reagir a mensagens (o atendente reagindo, igual o cliente já consegue)
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [localReaction, setLocalReaction] = useState(null);
  const currentReaction = localReaction ?? message.reaction_emoji;
  const handleReact = async (emoji) => {
    setShowReactionPicker(false);
    const newEmoji = currentReaction === emoji ? '' : emoji; // clica de novo no mesmo = remove
    setLocalReaction(newEmoji || null);
    try {
      await api.post(`/api/messages/${message.id}/react`, { emoji: newEmoji });
    } catch (err) {
      console.error('Erro ao reagir:', err);
      setLocalReaction(message.reaction_emoji || null); // desfaz otimismo se falhar
    }
  };
  const StatusIcon = () => {
    if (!isOutbound) return null;
    switch (message.status) {
      case 'sent': return <Check size={14} className="text-gray-400" />;
      case 'delivered': return <CheckCheck size={14} className="text-gray-400" />;
      case 'read': return <CheckCheck size={14} className="text-blue-500" />;
      default: return <Clock size={14} className="text-gray-400" />;
    }
  };

  const renderContent = () => {
    if (message.message_type === 'sticker') {
      // ⭐ NOVO: mostra a figurinha de verdade (sem moldura/fundo, igual WhatsApp)
      return message.media_url ? (
        <img
          src={message.media_url}
          alt="Figurinha"
          className="block w-[150px] h-[150px] object-contain"
        />
      ) : (
        <p className="text-sm italic opacity-70">Figurinha indisponível</p>
      );
    }
    if (message.message_type === 'image') {
      // NOVO: copia a imagem para a area de transferencia (util para
      // comprovantes de pagamento que o cliente manda no chat)
      const handleCopyImage = async (e) => {
        e.stopPropagation();
        try {
          const response = await fetch(message.media_url);
          const blob = await response.blob();
          // Clipboard API exige PNG na maioria dos navegadores - converte via canvas
          const pngBlob = await new Promise((resolve, reject) => {
            const img = new window.Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Falha ao converter imagem'))), 'image/png');
            };
            img.onerror = reject;
            img.src = URL.createObjectURL(blob);
          });
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': pngBlob })
          ]);
        } catch (err) {
          console.error('Erro ao copiar imagem:', err);
          alert('Não foi possível copiar a imagem. Seu navegador pode não suportar essa função.');
        }
      };

      return (
        <div>
          {/* ⭐ CORRIGIDO: imagem chegava muito pequena — bolha era estreita
              demais, então "max-w-full" acabava limitando bem pouco */}
          {message.media_url && (
            <div className="group relative">
              <img
                src={message.media_url}
                alt="Imagem"
                onClick={(e) => { e.stopPropagation(); setShowFullImage(true); }}
                className="rounded-lg w-full max-h-[420px] object-cover mb-1 cursor-pointer"
              />
              <button
                onClick={handleCopyImage}
                title="Copiar imagem"
                className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 bg-black/60 hover:bg-black/75 text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
              >
                <Copy size={13} />
                Copiar
              </button>
            </div>
          )}
          {message.body && !/^\[.*\]$/.test(message.body.trim()) && <p className="text-sm leading-relaxed whitespace-pre-wrap px-1 pb-1">{sanitizeMessage(message.body)}</p>}
        </div>
      );
    }
    if (message.message_type === 'audio') {
      return message.media_url ? (
        <AudioMessagePlayer message={message} isOutbound={isOutbound} conversation={conversation} currentUser={currentUser} />
      ) : (
        <p className="text-sm italic opacity-70">Áudio indisponível</p>
      );
    }
    if (message.message_type === 'video') {
      return (
        <div>
          {message.media_url && <video controls src={message.media_url} className="rounded-lg max-w-full mb-1" style={{ maxHeight: 300 }} />}
          {message.body && message.body !== '[Vídeo]' && <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{sanitizeMessage(message.body)}</p>}
        </div>
      );
    }
    if (message.message_type === 'document') {
      if (!message.media_url) {
        return <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{sanitizeMessage(message.body)}</p>;
      }
      const docLabel = sanitizeMessage(message.body) || 'Documento';
      const extension = (docLabel.split('.').pop() || 'PDF').toUpperCase().slice(0, 4);
      // ⭐ NOVO: miniatura da 1a pagina do PDF, igual o WhatsApp real mostra
      if (message.thumbnail_url) {
        return (
          <a
            href={message.media_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-xl overflow-hidden w-[200px]"
          >
            <div className="relative w-full h-[60px] overflow-hidden">
              <img src={message.thumbnail_url} alt={docLabel} className="w-full object-cover object-top" />
              {message.pdf_page_count > 1 && (
                <span className="absolute top-1.5 right-1.5 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                  1 de {message.pdf_page_count}
                </span>
              )}
            </div>
            <div className={`flex items-start gap-2 p-2.5 ${isOutbound ? 'bg-black/10' : 'bg-gray-100 dark:bg-slate-600'}`}>
              <span className="flex-shrink-0 mt-0.5 bg-red-500 text-white text-[9px] font-bold px-1 py-0.5 rounded leading-none">PDF</span>
              <div className="min-w-0">
                <p className="text-sm font-medium leading-snug">{docLabel}</p>
                <p className={`text-xs mt-0.5 ${isOutbound ? 'text-white/70' : 'text-gray-500 dark:text-slate-400'}`}>
                  {message.pdf_page_count > 1 ? `${message.pdf_page_count} páginas · ` : ''}{extension}{formatFileSize(message.media_size_bytes) ? ` · ${formatFileSize(message.media_size_bytes)}` : ''}
                </p>
              </div>
            </div>
          </a>
        );
      }
      return (
        <a
          href={message.media_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors min-w-[220px] max-w-[280px] ${
            isOutbound
              ? 'bg-black/10 hover:bg-black/15'
              : 'bg-gray-100 dark:bg-slate-600 hover:bg-gray-200 dark:hover:bg-slate-500'
          }`}
        >
          <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
            isOutbound ? 'bg-white/20' : 'bg-red-100 dark:bg-red-900/30'
          }`}>
            <FileText size={22} className={isOutbound ? 'text-white' : 'text-red-500 dark:text-red-400'} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{docLabel}</p>
            <p className={`text-xs ${isOutbound ? 'text-white/70' : 'text-gray-500 dark:text-slate-400'}`}>
              {extension}{formatFileSize(message.media_size_bytes) ? ` · ${formatFileSize(message.media_size_bytes)}` : ' · Toque para abrir'}
            </p>
          </div>
        </a>
      );
    }
    return <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{sanitizeMessage(message.body)}</p>;
  };

  // ⭐ NOVO: botões de template (Resposta Rápida, Link, Telefone), estilo WhatsApp
  const renderButtons = () => {
    if (!message.buttons || message.buttons.length === 0) return null;
    return (
      <div className={`mt-1.5 pt-1.5 border-t ${isOutbound ? 'border-green-500 dark:border-green-800' : 'border-gray-100 dark:border-slate-600'} space-y-1`}>
        {message.buttons.map((btn, idx) => {
          const isUrl = btn.type === 'URL';
          const isPhone = btn.type === 'PHONE_NUMBER';
          const Icon = isUrl ? ExternalLink : isPhone ? Phone : CornerDownLeft;
          const commonClasses = `w-full flex items-center justify-center gap-1.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            isOutbound
              ? 'text-white hover:bg-green-700 dark:hover:bg-green-900/40'
              : 'text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-slate-600'
          }`;
          if (isUrl && btn.url) {
            return (
              <a key={idx} href={btn.url} target="_blank" rel="noopener noreferrer" className={commonClasses}>
                <Icon size={14} />
                {btn.text}
              </a>
            );
          }
          if (isPhone && btn.phone_number) {
            return (
              <a key={idx} href={`tel:${btn.phone_number}`} className={commonClasses}>
                <Icon size={14} />
                {btn.text}
              </a>
            );
          }
          return (
            <div key={idx} className={commonClasses}>
              <Icon size={14} />
              {btn.text}
            </div>
          );
        })}
      </div>
    );
  };

  // NOVO: imagem fica "solta" (sem caixa/fundo) SOMENTE quando nao tem
  // legenda de texto real nem botao - se tiver qualquer um dos dois, precisa
  // da caixa com fundo para unir imagem + texto + botao visualmente
  const hasRealCaption = message.body && message.body.trim() && !/^\[.*\]$/.test(message.body.trim());
  const hasButtons = message.buttons && message.buttons.length > 0;
  const imageIsBare = message.message_type === 'image' && !hasRealCaption && !hasButtons;

  return (
    <>
    <div className={`flex w-full ${isOutbound ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className="group relative max-w-full flex-shrink-0">
        <div className={`relative ${
          message.message_type === 'sticker'
            ? 'w-[150px] max-w-[150px] bg-transparent p-0 shadow-none rounded-none'
            : message.message_type === 'audio'
              ? 'box-border w-[360px] max-w-[92%] min-h-[60px] min-w-0 overflow-visible px-3 py-2.5 mb-1 mr-1 rounded-[7.5px] shadow-sm'
              : message.message_type === 'image' && imageIsBare
                ? 'w-[380px] max-w-[90%] bg-transparent p-0 shadow-none rounded-lg'
                : message.message_type === 'image'
                  ? 'w-[380px] max-w-[90%] p-0 overflow-hidden rounded-lg shadow-sm'
                  : 'w-fit max-w-[420px] flex-shrink-0 px-3.5 py-2.5 rounded-lg shadow-sm'
        } ${
          message.message_type === 'sticker' || imageIsBare
            ? ''
            : isOutbound
              ? 'bg-green-600 dark:bg-[#005c4b] text-white rounded-tr-none'
              : 'bg-white dark:bg-slate-700 text-gray-800 dark:text-white rounded-tl-none'
        }`} >
          {renderContent()}
          {renderButtons()}
          {message.message_type !== 'audio' && (
          <div className={`flex items-center justify-end gap-1 ${
            message.message_type === 'sticker'
              ? 'absolute bottom-1.5 right-1.5 rounded-full bg-black/45 px-1.5 py-0.5 text-white'
              : message.message_type === 'audio'
                ? 'absolute bottom-2 right-2'
                : 'mt-1.5'
          } ${isOutbound ? 'text-green-100' : 'text-gray-400 dark:text-slate-300'}`}>
            <span className="text-[10px]">{new Date(message.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
            <StatusIcon />
          </div>
        )}
        </div>
        {/* ⭐ NOVO: reação grudada no canto da bolha, igual o WhatsApp de verdade */}
        {currentReaction && (
          // ⭐ CORRIGIDO: antes ficava colada no canto de dentro da bolha e podia
          // sobrepor o player de áudio — agora fica sempre por fora, embaixo,
          // sem risco de tampar nada do conteúdo
          <div className={`absolute -bottom-4 ${isOutbound ? 'left-1' : 'right-1'} bg-white dark:bg-slate-800 rounded-full shadow-md border border-gray-100 dark:border-slate-600 w-5 h-5 flex items-center justify-center text-[11px] z-10`}>
            {currentReaction}
          </div>
        )}
        {/* ⭐ NOVO: botão de reagir — só aparece ao passar o mouse, igual o
            WhatsApp real; clica de novo no mesmo emoji pra remover */}
        {message.wa_message_id && (
          <div className={`absolute top-0 ${isOutbound ? '-left-9' : '-right-9'} opacity-0 group-hover:opacity-100 transition-opacity`}>
            <button
              type="button"
              onClick={() => setShowReactionPicker(v => !v)}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-white dark:bg-slate-700 shadow-md border border-gray-100 dark:border-slate-600 text-gray-500 dark:text-slate-300 hover:scale-110 transition-transform"
              title="Reagir"
            >
              <SmilePlus size={14} />
            </button>
            {showReactionPicker && (
              <div className={`absolute top-8 ${isOutbound ? 'left-0' : 'right-0'} flex items-center gap-0.5 bg-white dark:bg-slate-700 rounded-full shadow-lg border border-gray-100 dark:border-slate-600 px-1.5 py-1 z-20`}>
                {QUICK_REACTIONS.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => handleReact(emoji)}
                    className={`w-7 h-7 flex items-center justify-center rounded-full text-base hover:bg-gray-100 dark:hover:bg-slate-600 hover:scale-125 transition-transform ${currentReaction === emoji ? 'bg-gray-100 dark:bg-slate-600' : ''}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    {/* NOVO: modal de imagem em tela cheia, igual clicar numa foto no WhatsApp.
        Renderizado via portal direto no document.body porque o container de
        mensagens (.whatsapp-chat-wallpaper) tem "isolation: isolate" + overflow,
        o que criava um contexto de empilhamento que prendia o modal "fixed"
        dentro dele, cortando-o e deixando a barra de digitação aparecer por cima. */}
    {showFullImage && message.media_url && createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
        onClick={() => setShowFullImage(false)}
      >
        <button
          onClick={() => setShowFullImage(false)}
          className="absolute top-4 right-4 text-white/80 hover:text-white"
          aria-label="Fechar"
        >
          <X size={28} />
        </button>
        <img
          src={message.media_url}
          alt="Imagem em tela cheia"
          className="max-h-full max-w-full object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>,
      document.body
    )}
    </>
  );
});

const WindowCountdown = ({ messages }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const inboundMessages = messages.filter(m => m.direction === 'inbound');

  if (inboundMessages.length === 0) {
    return (
      <p className="text-xs text-gray-400 dark:text-slate-500 flex items-center gap-1">
        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
        Aguardando primeira mensagem do cliente
      </p>
    );
  }

  const lastInbound = inboundMessages[inboundMessages.length - 1];
  const lastTime = new Date(lastInbound.timestamp).getTime();
  const expiresAt = lastTime + 24 * 60 * 60 * 1000;
  const remainingMs = expiresAt - now;

  if (remainingMs <= 0) {
    return (
      <p className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
        <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
        Janela de 24h expirada — use um template aprovado
      </p>
    );
  }

  const hours = Math.floor(remainingMs / (1000 * 60 * 60));
  const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
  const isWarning = remainingMs < 60 * 60 * 1000;

  return (
    <p className={`text-xs flex items-center gap-1 ${isWarning ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isWarning ? 'bg-amber-500' : 'bg-green-500'}`}></span>
      Janela 24h: {hours}h {minutes}m restantes
    </p>
  );
};

// ⭐ NOVO: período gratuito de anúncio CTWA (a Meta passou de 72 h para "até 7 dias" em
// 28/09/2026). É uma regra de COBRANÇA e independe da janela de 24 h: texto livre continua
// só dentro das 24 h; modelos ficam sem custo enquanto o período durar. O início é a primeira
// resposta da empresa que a Meta marcou como REFERRAL_CONVERSION — é uma aproximação; a
// confirmação de verdade é a cobrança no Gerenciador do WhatsApp.
const CTWA_FREE_MS = 7 * 24 * 60 * 60 * 1000;
const CtwaFreeWindow = ({ messages, isCtwa }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!isCtwa) return null;
  const respostas = (messages || []).filter(
    m => m.direction === 'outbound' && String(m.pricing_category || '').toUpperCase() === 'REFERRAL_CONVERSION'
  );
  if (respostas.length === 0) return null;
  const inicio = Math.min(...respostas.map(m => new Date(m.timestamp).getTime()));
  if (!Number.isFinite(inicio)) return null;
  const restanteMs = inicio + CTWA_FREE_MS - now;

  if (restanteMs <= 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1" title="O período gratuito do anúncio acabou. Modelos voltam a ser cobrados pela tabela normal.">
        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
        Período gratuito CTWA encerrado — modelos voltam a ser cobrados
      </p>
    );
  }

  const dias = Math.floor(restanteMs / (24 * 60 * 60 * 1000));
  const horas = Math.floor((restanteMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutos = Math.floor((restanteMs % (60 * 60 * 1000)) / (60 * 1000));
  const isWarning = restanteMs < 24 * 60 * 60 * 1000;
  const texto = dias > 0 ? `${dias}d ${horas}h` : `${horas}h ${minutos}m`;

  return (
    <p
      className={`text-xs flex items-center gap-1 ${isWarning ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}
      title="Modelos aprovados sem custo enquanto durar (a Meta fala em até 7 dias, contados da primeira resposta). Texto livre continua dependendo da janela de 24 h."
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isWarning ? 'bg-amber-500' : 'bg-green-500'}`}></span>
      Período gratuito CTWA: até {texto}
    </p>
  );
};

// ⭐ NOVO: modal de seleção de template pra enviar quando a janela está fechada
const TemplateSelectModal = ({ onClose, onSent, conversationId, waId, phoneNumberId }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getApprovedTemplates(phoneNumberId);
        setTemplates(res.data || []);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar os templates aprovados.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSend = async (templateId) => {
    setSendingId(templateId);
    try {
      await sendTemplateMessage(conversationId, waId, templateId);
      onSent();
    } catch (err) {
      alert('Erro ao enviar template: ' + (err.response?.data?.error || err.message));
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <FileText size={20} className="text-green-600" />
            Selecione um modelo
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <p className="text-xs text-gray-500 dark:text-slate-400 mb-4">
          A janela de 24h com este cliente está fechada. Escolha um template aprovado pela Meta para iniciar contato novamente.
        </p>

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-green-600" />
            </div>
          )}

          {!loading && error && (
            <p className="text-sm text-red-500 text-center py-4">{error}</p>
          )}

          {!loading && !error && templates.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Nenhum template aprovado encontrado.</p>
          )}

          {!loading && templates.map(tpl => (
            <div key={tpl.id} className="border border-gray-200 dark:border-slate-600 rounded-xl p-3 hover:border-green-400 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-sm text-gray-900 dark:text-slate-100">
                  {tpl.display_name || tpl.name}
                </span>
                <span className="text-[10px] px-2 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full font-medium">
                  {tpl.category || 'Utility'}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 line-clamp-2 mb-2 whitespace-pre-wrap">
                {tpl.body}
              </p>
              <button
                onClick={() => handleSend(tpl.id)}
                disabled={sendingId !== null}
                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {sendingId === tpl.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sendingId === tpl.id ? 'Enviando...' : 'Enviar este modelo'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function ConversationView({ id: propId, embedded = false }) {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const navigate = useNavigate();

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const inputRef = useRef(null); // NOVO: para inserir emoji na posição do cursor
  const [showEmojiPicker, setShowEmojiPicker] = useState(false); // NOVO: seletor de emojis completo

  // ⭐ NOVO: respostas rápidas — digitando "/" no campo de mensagem, mostra a lista
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [quickReplyFilter, setQuickReplyFilter] = useState('');
  const [showQuickReplyManager, setShowQuickReplyManager] = useState(false);
  const [newQuickReply, setNewQuickReply] = useState({ shortcut: '', title: '', body: '' });
  const [editingQuickReplyId, setEditingQuickReplyId] = useState(null);

  // ⭐ NOVO: abre o formulário já preenchido, pra editar uma resposta existente
  const openEditQuickReply = (qr) => {
    setNewQuickReply({ shortcut: qr.shortcut, title: qr.title || '', body: qr.body });
    setEditingQuickReplyId(qr.id);
    setShowQuickReplyManager(true);
    setShowQuickReplies(false);
  };

  const handleDeleteQuickReply = async (id) => {
    if (!window.confirm('Excluir essa resposta rápida?')) return;
    try {
      await deleteQuickReply(id);
      setQuickReplies(prev => prev.filter(qr => qr.id !== id));
    } catch (err) {
      alert('Erro ao excluir: ' + (err.response?.data?.error || err.message));
    }
  };

  useEffect(() => {
    getQuickReplies().then(res => setQuickReplies(res.data)).catch(err => console.error('Erro ao carregar respostas rápidas:', err));
  }, []);

  const filteredQuickReplies = quickReplies.filter(qr =>
    qr.shortcut.toLowerCase().includes(quickReplyFilter.toLowerCase())
  );

  const handleInputChange = (value) => {
    setInputText(value);
    const slashMatch = value.match(/(?:^|\s)\/(\S*)$/);
    if (slashMatch) {
      setQuickReplyFilter(slashMatch[1]);
      setShowQuickReplies(true);
    } else {
      setShowQuickReplies(false);
    }
  };

  // NOVO: insere o emoji escolhido na posição atual do cursor (ou no final,
  // se o input não estiver focado) e devolve o foco pro campo de texto
  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const el = inputRef.current;
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const newText = inputText.slice(0, start) + emoji + inputText.slice(end);
      setInputText(newText);
      requestAnimationFrame(() => {
        el.focus();
        const cursorPos = start + emoji.length;
        el.setSelectionRange(cursorPos, cursorPos);
      });
    } else {
      setInputText(prev => prev + emoji);
    }
  };

  const selectQuickReply = (qr) => {
    // ⭐ substitui o "/atalho" digitado pelo texto completo da resposta rápida
    const newText = inputText.replace(/(?:^|\s)\/(\S*)$/, (match) => (match.startsWith(' ') ? ' ' : '') + qr.body);
    setInputText(newText);
    setShowQuickReplies(false);
  };

  const closeQuickReplyManager = () => {
    setShowQuickReplyManager(false);
    setEditingQuickReplyId(null);
    setNewQuickReply({ shortcut: '', title: '', body: '' });
  };

  const handleCreateQuickReply = async () => {
    if (!newQuickReply.shortcut || !newQuickReply.body) {
      alert('Preencha o atalho e o texto');
      return;
    }
    try {
      if (editingQuickReplyId) {
        const res = await updateQuickReply(editingQuickReplyId, newQuickReply);
        setQuickReplies(prev => prev.map(qr => (qr.id === editingQuickReplyId ? res.data : qr)));
      } else {
        const res = await createQuickReply(newQuickReply);
        setQuickReplies(prev => [...prev, res.data]);
      }
      closeQuickReplyManager();
    } catch (err) {
      alert('Erro ao salvar resposta rápida: ' + (err.response?.data?.error || err.message));
    }
  };
  const [sending, setSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  // ⭐ CORRIGIDO: painel de Notas e Funil agora começa fechado — só abre
  // quando a pessoa clica no botão, em vez de aparecer sempre
  const [showSidebar, setShowSidebar] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showSearchCPF, setShowSearchCPF] = useState(false);
  const [showSearchClient, setShowSearchClient] = useState(false);
  const [cpfSearch, setCpfSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [searchType, setSearchType] = useState('cpf');
  const [showTemplateModal, setShowTemplateModal] = useState(false); // ⭐ NOVO
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false); // ⭐ NOVO
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [isRecordingPaused, setIsRecordingPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const streamRef = useRef(null);
  const recordingStartedAtRef = useRef(null);
  const recordingPausedAtRef = useRef(null);
  const recordingPausedTotalRef = useRef(0);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  // ⭐ NOVO: janela bloqueada = precisa de template
  const windowStatus = getWindowStatus(messages);
  const isBlocked = windowStatus.blocked;

  const loadData = async () => {
    try {
      const [convRes, msgRes] = await Promise.all([getConversation(id), getMessages(id)]);
      setConversation(convRes.data);
      setMessages(msgRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const loadCurrentUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const res = await api.get('/api/profile/me');
        const userData = res.data.user || {};

        setCurrentUser({
          id: payload.id,
          name: userData.name || payload.name,
          email: userData.email || payload.email,
          role: userData.role || payload.role,
          sector_id: userData.sector_id || payload.sector_id,
          sector_name: userData.sector_name || payload.sector_name,
          user_type: userData.user_type || payload.user_type,
          tenant_id: userData.tenant_id || payload.tenant_id,
          tenant_name: userData.tenant_name || payload.tenant_name,
          avatar_url: userData.avatar_url ? `${API}${userData.avatar_url}` : null,
        });
      } catch (err) {
        console.error('Erro ao carregar usuário autenticado:', err);
      }
    };

    loadCurrentUser();
  }, []);

  useEffect(() => {
    loadData();
    const i = setInterval(loadData, 5000);
    return () => clearInterval(i);
  }, [id]);

  // ⭐ CORRIGIDO: antes rolava pro final toda vez que "messages" mudava — como
  // loadData recarrega a cada 5s, isso puxava o usuário de volta pro final mesmo
  // enquanto ele estava lendo mensagens antigas mais acima. Agora só rola
  // automaticamente se o usuário já estava perto do final (ou é a 1ª carga).
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const wasNearBottom = distanceFromBottom < 150;
    if (wasNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !conversation || sending || isBlocked) return;
    setSending(true);
    try {
      await sendMessage(id, conversation.contact_wa_id, inputText);
      setInputText('');
      loadData();
    } catch (err) {
      alert('Erro ao enviar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleAttachClick = () => {
  if (uploadingFile || isRecording || isBlocked) return;
  setShowAttachMenu(false);
  fileInputRef.current?.click();
};

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !conversation || isBlocked) return;
    setUploadingFile(true);
    try {
      await sendMediaMessage(id, conversation.contact_wa_id, file, inputText);
      setInputText('');
      loadData();
    } catch (err) {
      alert('Erro ao enviar arquivo: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startRecording = async () => {
    if (uploadingFile || isBlocked) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setIsRecordingPaused(false);
      setRecordingTime(0);
      recordingStartedAtRef.current = Date.now();
      recordingPausedAtRef.current = null;
      recordingPausedTotalRef.current = 0;

      recordingIntervalRef.current = setInterval(() => {
        const startedAt = recordingStartedAtRef.current;
        if (!startedAt) return;
        const now = recordingPausedAtRef.current || Date.now();
        const pausedTotal = recordingPausedTotalRef.current || 0;
        const elapsedMs = Math.max(0, now - startedAt - pausedTotal);
        setRecordingTime(Math.floor(elapsedMs / 1000));
      }, 250);
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
      alert('Não foi possível acessar o microfone. Verifique as permissões do navegador.');
    }
  };

  const toggleRecordingPause = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    if (recorder.state === 'recording') {
      recorder.pause();
      recordingPausedAtRef.current = Date.now();
      setIsRecordingPaused(true);
      return;
    }

    if (recorder.state === 'paused') {
      if (recordingPausedAtRef.current) {
        recordingPausedTotalRef.current += Date.now() - recordingPausedAtRef.current;
      }
      recordingPausedAtRef.current = null;
      recorder.resume();
      setIsRecordingPaused(false);
    }
  };

  const stopRecordingAndSend = () => {
    if (!mediaRecorderRef.current) return;

    mediaRecorderRef.current.onstop = async () => {
      clearInterval(recordingIntervalRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      setIsRecording(false);
      setIsRecordingPaused(false);
      recordingStartedAtRef.current = null;
      recordingPausedAtRef.current = null;
      recordingPausedTotalRef.current = 0;

      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

      if (audioBlob.size === 0 || recordingTime < 1) {
        setRecordingTime(0);
        return;
      }

      const audioFile = new File([audioBlob], `gravacao-${Date.now()}.webm`, { type: 'audio/webm' });

      setUploadingFile(true);
      try {
        await sendMediaMessage(id, conversation.contact_wa_id, audioFile, '');
        loadData();
      } catch (err) {
        alert('Erro ao enviar áudio: ' + (err.response?.data?.error || err.message));
      } finally {
        setUploadingFile(false);
        setRecordingTime(0);
      }
    };

    mediaRecorderRef.current.stop();
  };

  const cancelRecording = () => {
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.onstop = () => {
      clearInterval(recordingIntervalRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      setIsRecording(false);
      setIsRecordingPaused(false);
      recordingStartedAtRef.current = null;
      recordingPausedAtRef.current = null;
      recordingPausedTotalRef.current = 0;
      setRecordingTime(0);
      audioChunksRef.current = [];
    };
    mediaRecorderRef.current.stop();
  };

  const formatRecordingTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleTransferSuccess = () => {
    loadData();
    alert('✅ Conversa transferida com sucesso!');
  };

  const handleTemplateSent = () => {
    setShowTemplateModal(false);
    loadData();
  };

  if (!conversation) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // ⭐ NOVO: troca o status da conversa (Aberto/Pendente/Resolvido)
  const STATUS_OPTIONS = [
    { key: 'open', label: 'Aberto', color: '#2563EB' },
    { key: 'pending', label: 'Pendente', color: '#F59E0B' },
    { key: 'closed', label: 'Resolvido', color: '#16A34A' },
  ];
  const activeStatusOption = STATUS_OPTIONS.find(o => o.key === conversation.status) || STATUS_OPTIONS[0];
  const handleStatusChange = async (newStatus) => {
    setShowStatusMenu(false);
    if (newStatus === conversation.status) return;
    setUpdatingStatus(true);
    try {
      await api.put(`/api/conversations/${conversation.id}/status`, { status: newStatus });
      setConversation(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      alert('Erro ao atualizar status da conversa');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ⭐ NOVO: descadastra o cliente — some da lista do atendente, mas continua
  // visível pro admin/gerente com a etiqueta "Descadastrado", e vai pro funil
  // de vendas na etapa "Descadastrado"
  const handleUnsubscribe = async () => {
    setShowStatusMenu(false);
    if (!window.confirm('Descadastrar esse cliente? Ele vai sumir da sua lista de conversas e ir pro funil de vendas como "Descadastrado".')) return;
    setUpdatingStatus(true);
    try {
      await api.put(`/api/conversations/${conversation.id}/unsubscribe`);
      navigate(-1);
    } catch (err) {
      console.error('Erro ao descadastrar:', err);
      alert('Erro ao descadastrar conversa');
      setUpdatingStatus(false);
    }
  };

  return (
    <>
    <div className="flex h-full bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className={`flex flex-col flex-1 min-w-0 overflow-hidden ${showSidebar ? 'md:mr-0' : ''}`}>
          <div className="px-6 py-4 border-b border-gray-50 dark:border-slate-700 flex items-center justify-between bg-white dark:bg-slate-800 flex-shrink-0">
            <div className="flex items-center gap-4">
              {!embedded && (
                <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-full text-gray-500 dark:text-slate-400">
                  <ChevronLeft size={20} />
                </button>
              )}
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center justify-center font-bold">
                {(conversation.profile_name || 'U').charAt(0)}
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-slate-100">{conversation.profile_name || 'Usuário'}</h3>
                <>
                <WindowCountdown messages={messages} />
                <CtwaFreeWindow messages={messages} isCtwa={!!conversation?.ctwa_clid} />
              </>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* ⭐ NOVO: seletor de status Aberto/Pendente/Resolvido */}
              <div className="relative" data-status-menu>
                <button
                  type="button"
                  onClick={() => setShowStatusMenu(v => !v)}
                  disabled={updatingStatus}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: activeStatusOption.color }} />
                  <span className="text-gray-700 dark:text-slate-200">{activeStatusOption.label}</span>
                </button>
                {showStatusMenu && (
                  <div className="absolute right-0 top-11 z-20 w-40 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg py-1">
                    {STATUS_OPTIONS.map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => handleStatusChange(opt.key)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-slate-600 ${
                          conversation.status === opt.key ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                        }`}
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: opt.color }} />
                        <span className="text-gray-800 dark:text-slate-100">{opt.label}</span>
                      </button>
                    ))}
                    <div className="border-t border-gray-100 dark:border-slate-600 my-1" />
                    <button
                      type="button"
                      onClick={handleUnsubscribe}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <UserX size={14} className="flex-shrink-0" />
                      <span>Descadastrar</span>
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => setShowTransferModal(true)} className="p-2 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-full text-gray-400 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" title="Transferir conversa">
                <ArrowRightLeft size={20} />
              </button>
              <button onClick={() => setShowSidebar(!showSidebar)} className={`p-2 rounded-full transition-colors ${showSidebar ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' : 'hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-400 dark:text-slate-400'}`} title="Notas e Funil">
                <StickyNote size={20} />
              </button>
              <button className="p-2 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-full text-gray-400 dark:text-slate-400 transition-colors" title="Chamada">
                <Phone size={20} />
              </button>
              <div className="relative" data-more-menu>
                <button
                  type="button"
                  onClick={() => setShowMoreMenu(v => !v)}
                  className="p-2 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-full text-gray-400 dark:text-slate-400 transition-colors"
                  title="Mais opções"
                >
                  <MoreVertical size={20} />
                </button>
                {showMoreMenu && (
                  <div className="absolute right-0 top-11 z-20 w-64 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg py-2 px-3">
                    <p className="text-[11px] text-gray-400 dark:text-slate-400 uppercase font-medium mb-1">Número do cliente</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800 dark:text-slate-100">
                        {stripCountryCode(conversation.contact_wa_id) || 'Não disponível'}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(stripCountryCode(conversation.contact_wa_id));
                          setPhoneCopied(true);
                          setTimeout(() => setPhoneCopied(false), 2000);
                        }}
                        className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-xs font-medium hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors flex-shrink-0"
                      >
                        <Copy size={12} />
                        {phoneCopied ? 'Copiado!' : 'Copiar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
  ref={messagesContainerRef}
  className="whatsapp-chat-wallpaper flex-1 overflow-y-auto p-6"
  style={{ backgroundImage: "url('/whatsapp-wallpaper.png')" }}
>

  {/* ⭐ NOVO: camada separada e sutil pro papel de parede no tema escuro (a
      opacidade é controlada via CSS, a imagem via style inline igual acima) */}
  <div
    className="whatsapp-chat-wallpaper__pattern"
    style={{ backgroundImage: "url('/whatsapp-wallpaper.png')" }}
  />

  <div className="whatsapp-chat-wallpaper__messages w-full">
    {/* ⭐ NOVO: cartão de referência do anúncio — só aparece se o contato veio de
        um anúncio "Clique para WhatsApp" (CTWA) */}
    {conversation.ctwa_clid && (
      <div className="max-w-md mx-auto mb-3 bg-white dark:bg-slate-700 border border-orange-200 dark:border-orange-800 rounded-xl overflow-hidden shadow-sm">
        {/* ⭐ NOVO: miniatura do anúncio, quando a Meta manda essa informação */}
        {conversation.campaign_image_url && (
          <img
            src={conversation.campaign_image_url}
            alt="Anúncio"
            className="w-full max-h-48 object-cover"
          />
        )}
        <div className="p-3">
          <div className="flex items-center gap-1.5 text-orange-600 dark:text-orange-400 text-xs font-bold mb-1.5">
            <Megaphone size={12} />
            Veio de um anúncio patrocinado
          </div>
          {conversation.campaign_name && (
            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{conversation.campaign_name}</p>
          )}
          {conversation.campaign_body && (
            <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">{conversation.campaign_body}</p>
          )}
          {conversation.campaign_source_url && (
            <a href={conversation.campaign_source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1.5 inline-block">
              {conversation.campaign_source_url}
            </a>
          )}
        </div>
      </div>
    )}
    {messages
      .filter(msg => msg.status !== 'failed')
      .map((msg, idx, arr) => {
        const prevMsg = idx > 0 ? arr[idx - 1] : null;
        const showDivider = !prevMsg || new Date(prevMsg.timestamp).toDateString() !== new Date(msg.timestamp).toDateString();
        return (
          <React.Fragment key={msg.id}>
            {showDivider && (
              <div className="flex justify-center my-3">
                <span className="bg-white/90 dark:bg-slate-700/90 text-gray-600 dark:text-slate-200 text-xs font-medium px-3 py-1 rounded-lg shadow-sm capitalize">
                  {getDateDividerLabel(msg.timestamp)}
                </span>
              </div>
            )}
            <MessageBubble message={msg} conversation={conversation} currentUser={currentUser} />
          </React.Fragment>
        );
      })}
    <div ref={messagesEndRef} />
  </div>
</div>

          <div className="p-4 bg-white dark:bg-slate-800 border-t border-gray-50 dark:border-slate-700 flex-shrink-0">
            {isBlocked ? (
              // ⭐ NOVO: barra de bloqueio quando a janela de 24h está fechada
              <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3">
                <Lock size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    Este é um chat fora da janela de campanha
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Até que você receba uma mensagem do cliente, o WhatsApp permite apenas o envio de templates aprovados.
                  </p>
                </div>
                {currentUser?.role !== 'attendant' && (
                  <button
                    onClick={() => setShowTemplateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 transition-all flex-shrink-0"
                  >
                    <FileText size={16} />
                    Selecione o modelo
                  </button>
                )}
              </div>
            ) : isRecording ? (
              <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-slate-600 dark:bg-slate-800">
                <button type="button" onClick={cancelRecording} className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20" title="Excluir gravação" aria-label="Excluir gravação"><Trash2 size={20} /></button>
                <button type="button" onClick={toggleRecordingPause} className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-100 dark:text-slate-100 dark:hover:bg-slate-700" title={isRecordingPaused ? 'Continuar gravando' : 'Pausar gravação'} aria-label={isRecordingPaused ? 'Continuar gravando' : 'Pausar gravação'}>{isRecordingPaused ? <Play size={20} fill="currentColor" /> : <Pause size={20} fill="currentColor" />}</button>
                <div className="flex min-w-0 flex-1 items-center gap-3 px-1"><span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${isRecordingPaused ? 'bg-gray-400' : 'bg-[#e91e63] animate-pulse'}`}></span><div className="flex min-w-0 flex-1 items-center gap-[1px] overflow-hidden">{VOICE_WAVEFORM.map((height, index) => <span key={index} className={`w-[2px] flex-shrink-0 rounded-full transition-colors ${isRecordingPaused ? 'bg-gray-300 dark:bg-slate-600' : 'bg-gray-500 dark:bg-slate-400'}`} style={{ height: `${Math.max(6, height - 3)}px` }}></span>)}</div><span className="flex-shrink-0 text-sm tabular-nums text-gray-600 dark:text-slate-300">{formatRecordingTime(recordingTime)}</span><Mic size={20} className="flex-shrink-0 text-[#e91e63]" /></div>
                <button type="button" onClick={stopRecordingAndSend} disabled={uploadingFile} className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#00a884] text-white shadow-sm transition-colors hover:bg-[#008f72] disabled:opacity-50" title="Enviar áudio" aria-label="Enviar áudio">{uploadingFile ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}</button>
              </div>
            ) : (
              <form
  onSubmit={handleSend}
  className="relative flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-slate-600 dark:bg-slate-800"
>
  <input
    type="file"
    ref={fileInputRef}
    onChange={(e) => {
      setShowAttachMenu(false);
      handleFileChange(e);
    }}
    className="hidden"
    accept="image/*,audio/*,video/*,.pdf,.doc,.docx"
  />

  <button
    type="button"
    onClick={() => setShowAttachMenu((visible) => !visible)}
    disabled={uploadingFile}
    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-700"
    title="Anexar"
    aria-label="Abrir opções de anexos"
  >
    {showAttachMenu ? <X size={22} strokeWidth={2} /> : <Plus size={23} strokeWidth={2} />}
  </button>

  {/* NOVO: botão de emoji, abre o seletor completo (categorias, busca, recentes) */}
  <button
    type="button"
    onClick={() => setShowEmojiPicker((visible) => !visible)}
    disabled={uploadingFile}
    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-700"
    title="Emoji"
    aria-label="Abrir seletor de emojis"
  >
    <SmilePlus size={20} strokeWidth={2} />
  </button>

  {showEmojiPicker && (
    <div className="absolute bottom-full left-0 z-30 mb-3">
      <EmojiPicker
        onEmojiClick={handleEmojiClick}
        autoFocusSearch={false}
        theme="auto"
        searchPlaceholder="Buscar emoji"
        width={320}
        height={400}
        previewConfig={{ showPreview: false }}
      />
    </div>
  )}

  {showAttachMenu && (
    <div className="absolute bottom-full left-0 z-30 mb-3 w-52 overflow-hidden rounded-2xl border border-gray-100 bg-white p-2 shadow-xl dark:border-slate-700 dark:bg-slate-800">
      <button type="button" onClick={handleAttachClick} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
        <FileText size={19} className="text-[#7c4dff]" />
        Documento
      </button>
      <button type="button" onClick={handleAttachClick} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
        <ImageIcon size={19} className="text-[#168cff]" />
        Fotos e vídeos
      </button>
      <button type="button" onClick={handleAttachClick} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
        <Camera size={19} className="text-[#e83e8c]" />
        Câmera
      </button>
      <button type="button" onClick={handleAttachClick} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
        <Headphones size={19} className="text-[#ef5350]" />
        Áudio
      </button>
      <button type="button" onClick={() => setShowAttachMenu(false)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
        <UserRound size={19} className="text-[#2196f3]" />
        Contato
      </button>
      <button type="button" onClick={() => setShowAttachMenu(false)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
        <ListChecks size={19} className="text-[#f4b400]" />
        Enquete
      </button>
      <button type="button" onClick={() => setShowAttachMenu(false)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
        <CalendarDays size={19} className="text-[#e91e63]" />
        Evento
      </button>
      <button type="button" onClick={() => setShowAttachMenu(false)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700">
        <SmilePlus size={19} className="text-[#00bfa5]" />
        Nova figurinha
      </button>
    </div>
  )}

  <textarea
    rows={Math.min(Math.max(inputText.split('\n').length, 1), 8)}
    value={inputText}
    onChange={e => handleInputChange(e.target.value)}
    onKeyDown={e => {
      // ⭐ NOVO: se a lista de respostas rápidas estiver aberta, Enter escolhe
      // a primeira opção em vez de mandar o "/atalho" cru como mensagem
      if (e.key === 'Enter' && showQuickReplies) {
        e.preventDefault();
        if (filteredQuickReplies.length > 0) {
          selectQuickReply(filteredQuickReplies[0]);
        }
      } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        // textarea não envia o form com Enter sozinho: dispara o envio manualmente
        e.preventDefault();
        e.currentTarget.form?.requestSubmit();
      }
    }}
    placeholder={uploadingFile ? 'Enviando...' : 'Digite uma mensagem. Comece com "/" para escolher uma resposta rápida'}
    disabled={uploadingFile}
    ref={inputRef}
    onFocus={() => setShowEmojiPicker(false)}
    className="min-w-0 flex-1 resize-none overflow-y-auto leading-5 border-none bg-transparent px-1 py-2 text-sm text-gray-800 outline-none placeholder:text-gray-400 focus:ring-0 dark:text-slate-100 dark:placeholder-slate-400 disabled:opacity-60"
  />
  {/* ⭐ NOVO: lista de respostas rápidas, aparece ao digitar "/" */}
  {showQuickReplies && (
    <div className="absolute bottom-full left-0 right-0 mb-2 max-h-56 overflow-y-auto bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg z-20">
      {filteredQuickReplies.length === 0 ? (
        <div className="p-3 text-sm text-gray-400 dark:text-slate-500">
          Nenhuma resposta rápida encontrada.
        </div>
      ) : (
        filteredQuickReplies.map(qr => (
          <div key={qr.id} className="flex items-center gap-1 px-1 hover:bg-gray-50 dark:hover:bg-slate-600 border-b border-gray-50 dark:border-slate-600">
            <button
              type="button"
              onClick={() => selectQuickReply(qr)}
              className="flex-1 min-w-0 text-left px-2 py-2"
            >
              <p className="text-xs font-bold text-blue-600 dark:text-blue-400">/{qr.shortcut}</p>
              <p className="text-sm text-gray-700 dark:text-slate-200 truncate">{qr.body}</p>
            </button>
            <button type="button" onClick={() => openEditQuickReply(qr)} title="Editar" className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg flex-shrink-0">
              <Pencil size={13} />
            </button>
            <button type="button" onClick={() => handleDeleteQuickReply(qr.id)} title="Excluir" className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg flex-shrink-0">
              <Trash2 size={13} />
            </button>
          </div>
        ))
      )}
      {/* ⭐ NOVO: sempre disponível, pra dar pra criar mais respostas a qualquer momento */}
      <button
        type="button"
        onClick={() => { setShowQuickReplyManager(true); setShowQuickReplies(false); }}
        className="w-full text-left px-3 py-2.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2"
      >
        <Plus size={14} />
        Nova resposta rápida
      </button>
    </div>
  )}

  {inputText.trim() ? (
    <button
      type="submit"
      disabled={sending || uploadingFile}
      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#00a884] text-white transition-colors hover:bg-[#008f72] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
      title="Enviar mensagem"
    >
      {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
    </button>
  ) : (
    <button
      type="button"
      onClick={startRecording}
      disabled={uploadingFile}
      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:text-slate-100 dark:hover:bg-slate-700"
      title="Gravar áudio"
      aria-label="Gravar áudio"
    >
      {uploadingFile ? <Loader2 size={20} className="animate-spin" /> : <Mic size={21} strokeWidth={2.1} />}
    </button>
  )}
</form>
            )}
          </div>
        </div>

        {showSidebar && (
          <div className="w-64 2xl:w-80 border-l border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col flex-shrink-0">
            <FunnelStageSelector conversationId={id} currentStageId={conversation.funnel_stage_id} />
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <AppointmentBooking conversationId={id} contactName={conversation.profile_name} />
            </div>
            <div className="p-3 border-t border-gray-100 dark:border-slate-700 space-y-2">
              <button onClick={() => setShowSearchCPF(true)} className="w-full flex items-center gap-3 px-3 py-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                <Search size={18} />
                <span className="text-sm font-medium">Pesquisar CPF</span>
              </button>
              <button onClick={() => setShowSearchClient(true)} className="w-full flex items-center gap-3 px-3 py-2.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors">
                <UserSearch size={18} />
                <span className="text-sm font-medium">Pesquisar Cliente</span>
              </button>
            </div>
          </div>
        )}

        {showQuickReplyManager && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100">{editingQuickReplyId ? 'Editar' : 'Nova'} Resposta Rápida</h3>
                <button type="button" onClick={closeQuickReplyManager} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Atalho (sem espaço, ex: bomdia)</label>
                  <input type="text" value={newQuickReply.shortcut}
                    onChange={e => setNewQuickReply({ ...newQuickReply, shortcut: e.target.value })}
                    placeholder="bomdia"
                    className="w-full p-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 rounded-lg text-sm text-gray-900 dark:text-slate-100" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Texto completo</label>
                  <textarea value={newQuickReply.body}
                    onChange={e => setNewQuickReply({ ...newQuickReply, body: e.target.value })}
                    rows={4}
                    placeholder="Bom dia! Em que posso ajudar?"
                    className="w-full p-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 rounded-lg text-sm text-gray-900 dark:text-slate-100" />
                </div>
                <button
                  type="button"
                  onClick={handleCreateQuickReply}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}
        {showSearchCPF && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                  <Search size={20} className="text-blue-600" />
                  Pesquisar por CPF
                </h3>
                <button onClick={() => setShowSearchCPF(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Digite o CPF do cliente</label>
                  <input type="text" value={cpfSearch} onChange={(e) => setCpfSearch(e.target.value)} placeholder="000.000.000-00" className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:bg-slate-700 dark:text-slate-100" />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowSearchCPF(false)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700">Cancelar</button>
                  <button onClick={() => { alert(`Buscando CPF: ${cpfSearch}`); setShowSearchCPF(false); }} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700">Pesquisar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSearchClient && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                  <UserSearch size={20} className="text-purple-600" />
                  Pesquisar Cliente
                </h3>
                <button onClick={() => setShowSearchClient(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Tipo de Busca</label>
                  <div className="flex gap-2 mb-3">
                    <button onClick={() => setSearchType('cpf')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${searchType === 'cpf' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400'}`}>CPF</button>
                    <button onClick={() => setSearchType('name')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${searchType === 'name' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400'}`}>Nome</button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">{searchType === 'cpf' ? 'Digite o CPF' : 'Digite o Nome'}</label>
                  <input type="text" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder={searchType === 'cpf' ? '000.000.000-00' : 'Nome do cliente'} className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none dark:bg-slate-700 dark:text-slate-100" />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowSearchClient(false)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700">Cancelar</button>
                  <button onClick={() => { alert(`Buscando ${searchType === 'cpf' ? 'CPF' : 'Nome'}: ${clientSearch}`); setShowSearchClient(false); }} className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700">Pesquisar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showTransferModal && (
        <TransferModal conversationId={id} currentSectorId={conversation.sector_id} onClose={() => setShowTransferModal(false)} onSuccess={handleTransferSuccess} />
      )}

      {showTemplateModal && (
        <TemplateSelectModal
          conversationId={id}
          waId={conversation.contact_wa_id}
          phoneNumberId={conversation.phone_number_id}
          onClose={() => setShowTemplateModal(false)}
          onSent={handleTemplateSent}
        />
      )}
    </>
  );
}

export default ConversationView;
